import { useCallback, useEffect, useRef, useState } from 'react'

interface SpeechRecognitionLike {
  lang: string
  continuous: boolean
  interimResults: boolean
  start: () => void
  stop: () => void
  onresult: ((event: any) => void) | null
  onerror: ((event: any) => void) | null
  onend: (() => void) | null
}

type RecognitionCtor = new () => SpeechRecognitionLike

interface UseAiVoiceOptions {
  lang?: string
  onTranscript: (text: string) => void
}

/**
 * Web Speech API voice input (browser-side STT — zero marginal cost,
 * en/ar recognition). Falls back gracefully when unsupported.
 */
export function useAiVoice({ lang, onTranscript }: UseAiVoiceOptions) {
  const [isListening, setIsListening] = useState(false)
  const [supported, setSupported] = useState(false)
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null)
  const transcriptRef = useRef('')
  // Callers pass inline callbacks (AiCopilotDrawer does), so a live session must
  // read the LATEST one — the recognition handler outlives `start`'s closure.
  const onTranscriptRef = useRef(onTranscript)
  useEffect(() => {
    onTranscriptRef.current = onTranscript
  }, [onTranscript])

  useEffect(() => {
    const w = window as any
    const ctor: RecognitionCtor | undefined = w.SpeechRecognition ?? w.webkitSpeechRecognition
    setSupported(Boolean(ctor))
  }, [])

  const stop = useCallback(() => {
    recognitionRef.current?.stop()
    setIsListening(false)
  }, [])

  const start = useCallback(() => {
    const w = window as any
    const ctor: RecognitionCtor | undefined = w.SpeechRecognition ?? w.webkitSpeechRecognition

    if (!ctor) return

    // Only one recognition may exist at a time: detach and stop any previous
    // instance first, or a rapid restart leaks the old capture (its mic stream
    // and listeners stay alive) and Recognition.start() throws
    // InvalidStateError while a session is still running.
    if (recognitionRef.current) {
      const previous = recognitionRef.current
      previous.onresult = null
      previous.onerror = null
      previous.onend = null
      try {
        previous.stop()
      } catch {
        // Already ended/never started — nothing left to tear down.
      }
      recognitionRef.current = null
    }

    const recognition = new ctor()
    recognition.lang = lang === 'ar' ? 'ar-SA' : 'en-US'
    recognition.continuous = false
    recognition.interimResults = false
    transcriptRef.current = ''

    recognition.onresult = (event: any) => {
      let transcript = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript
      }
      transcriptRef.current = transcript.trim()
      if (transcriptRef.current) {
        onTranscriptRef.current(transcriptRef.current)
      }
    }

    recognition.onerror = () => setIsListening(false)
    recognition.onend = () => setIsListening(false)

    recognitionRef.current = recognition
    setIsListening(true)
    recognition.start()
  }, [lang])

  useEffect(() => () => recognitionRef.current?.stop(), [])

  return { isListening, supported, start, stop }
}
